import graphql__4b425e93134d553b3673 from "./__generated__/EmailTemplateVariantPanelUpdateMutation.graphql.ts";import * as React from "react";
import {useState, useCallback} from "react";
import {useIntl} from "react-intl";
import {graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {Form, Formik, Field, useFormikContext} from "formik";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faStar, faTrash, faWandMagicSparkles} from "@fortawesome/pro-regular-svg-icons";
import VisualBlockEditor from "./VisualBlockEditor";
import SaveButton from "../presentational/button/SaveButton";
import {useErrorContext} from "../../contexts/ErrorContext";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {blocksToPlainText} from "../../helper/blocksToPlainText";

interface EmailTemplateImage {
    id: string;
    emailTemplateImageIdentifier: string;
    url: string;
    originalFilename: string;
    width: number;
    height: number;
    mediaType: string;
    fileSize: number;
}

interface EmailTemplateVariantPanelProps {
    emailTemplateId: string;
    language: string;
    subject: string;
    preheader: string;
    htmlContent: string;
    textContent: string;
    layoutTextContent: string;
    isPrimary: boolean;
    canRemove: boolean;
    images: EmailTemplateImage[];
    onSetPrimary: () => void;
    onRemove: () => void;
}

/**
 * Replace footer variables with preview values for display
 */
function replaceFooterVariablesForPreview(text: string): string {
    const currentYear = new Date().getFullYear();
    const previewValues: Record<string, string> = {
        'subscriptionReason': 'Du bekommst diese E-Mail, weil du den Newsletter abonniert hast.',
        'copyrightNotice': `© Copyright ${currentYear} Flownative`,
        'companyAddress': 'Flownative GmbH, Arnimstraße 19 C, 23566 Lübeck',
        'managePreferencesUrl': 'https://example.com/unsubscribe',
        'managePreferencesText': 'E-Mail-Einstellungen verwalten',
        'imprintUrl': 'https://www.flownative.com/de/impressum.html',
        'imprintText': 'Impressum',
        'privacyPolicyUrl': 'https://www.flownative.com/de/datenschutz.html',
        'privacyPolicyText': 'Datenschutz',
    };

    let result = text;
    for (const [key, value] of Object.entries(previewValues)) {
        result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    }
    return result;
}

/**
 * Parse text layout to extract content before and after {{content}} placeholder
 */
function parseTextLayout(layoutText: string): { before: string; after: string } {
    if (!layoutText || !layoutText.includes('{{content}}')) {
        return { before: '', after: '' };
    }
    const [before, after] = layoutText.split('{{content}}');
    return {
        before: replaceFooterVariablesForPreview((before || '').trim()),
        after: replaceFooterVariablesForPreview((after || '').trim())
    };
}

type ContentTab = "visual" | "text";

interface FormValues {
    subject: string;
    preheader: string;
    htmlContent: string;
    textContent: string;
}

// Inner component to access Formik context - always editable
const EditorContent: React.FC<{
    contentTab: ContentTab;
    emailTemplateId: string;
    language: string;
    layoutTextContent: string;
    images: EmailTemplateImage[];
}> = ({contentTab, emailTemplateId, language, layoutTextContent, images}) => {
    const intl = useIntl();
    const {values, setFieldValue} = useFormikContext<FormValues>();

    const handleBlockEditorChange = useCallback((value: string) => {
        setFieldValue("htmlContent", value);
    }, [setFieldValue]);

    const handleGenerateFromHtml = useCallback(() => {
        const plainText = blocksToPlainText(values.htmlContent);
        setFieldValue("textContent", plainText);
    }, [values.htmlContent, setFieldValue]);

    if (contentTab === "visual") {
        return (
            <VisualBlockEditor
                value={values.htmlContent}
                onChange={handleBlockEditorChange}
                emailTemplateId={emailTemplateId}
                language={language}
                images={images}
            />
        );
    }

    // Text tab - always editable
    const layoutParts = parseTextLayout(layoutTextContent);
    const hasLayoutPreview = layoutParts.before || layoutParts.after;

    return (
        <div className="p-4">
            <div className="mb-3">
                <button
                    type="button"
                    onClick={handleGenerateFromHtml}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    <FontAwesomeIcon icon={faWandMagicSparkles} className="w-4 h-4" />
                    {intl.formatMessage({id: "emailTemplate.generateFromHtml"})}
                </button>
            </div>

            {hasLayoutPreview && (
                <p className="text-xs text-gray-500 mb-2 italic">
                    {intl.formatMessage({id: "emailTemplate.layoutPreviewHint"})}
                </p>
            )}

            {/* Layout header (before {{content}}) */}
            {layoutParts.before && (
                <pre className="mb-2 px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-t-md border border-b-0 border-gray-300 whitespace-pre-wrap"
                     style={{fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace"}}>
                    {layoutParts.before}
                </pre>
            )}

            <Field
                as="textarea"
                name="textContent"
                className={`w-full min-h-[400px] px-3 py-2 text-sm border border-gray-300 focus:ring-1 focus:ring-red-500 focus:border-red-500 ${
                    layoutParts.before && layoutParts.after ? '' :
                    layoutParts.before ? 'rounded-b-md' :
                    layoutParts.after ? 'rounded-t-md' :
                    'rounded-md'
                }`}
                style={{fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace"}}
                placeholder={intl.formatMessage({id: "emailTemplate.placeholder.textContent"})}
            />

            {/* Layout footer (after {{content}}) */}
            {layoutParts.after && (
                <pre className="mt-0 px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-b-md border border-t-0 border-gray-300 whitespace-pre-wrap"
                     style={{fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace"}}>
                    {layoutParts.after}
                </pre>
            )}
        </div>
    );
};

const EmailTemplateVariantPanel: React.FC<EmailTemplateVariantPanelProps> = ({
    emailTemplateId,
    language,
    subject,
    preheader,
    htmlContent,
    textContent,
    layoutTextContent,
    isPrimary,
    canRemove,
    images,
    onSetPrimary,
    onRemove,
}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const [contentTab, setContentTab] = useState<ContentTab>("visual");

    const handleSubmit = useCallback(async (values: FormValues) => {
        return new Promise<void>((resolve, reject) => {
            commitMutationWithErrorHandling(
                relayEnvironment,
                {
                    mutation: graphql__4b425e93134d553b3673,
                    variables: {
                        input: {
                            id: emailTemplateId,
                            language,
                            subject: values.subject,
                            preheader: values.preheader || null,
                            htmlContent: values.htmlContent,
                            textContent: values.textContent
                        }
                    },
                    onCompleted: () => {
                        resolve();
                    },
                    onError: (error) => {
                        reject(error);
                    }
                },
                addError,
                {
                    customErrorMessage: "errors.failedToUpdateEmailTemplateVariant"
                }
            );
        });
    }, [relayEnvironment, emailTemplateId, language, addError]);

    const ContentTabButton: React.FC<{
        tab: ContentTab;
        label: string;
    }> = ({tab, label}) => (
        <button
            type="button"
            onClick={() => setContentTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                contentTab === tab
                    ? "border-red-600 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
        >
            {label}
        </button>
    );

    return (
        <Formik
            initialValues={{
                subject: subject || "",
                preheader: preheader || "",
                htmlContent: htmlContent || "",
                textContent: textContent || "",
            }}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({isSubmitting, dirty}) => (
                <Form>
                    {/* Subject and Preheader section - always editable */}
                    <div className="border-b border-gray-200 px-4 py-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {intl.formatMessage({id: 'emailTemplate.field.subject'})}
                                    </label>
                                    <Field
                                        name="subject"
                                        type="text"
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                        placeholder={intl.formatMessage({id: 'emailTemplate.placeholder.subject'})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {intl.formatMessage({id: 'emailTemplate.field.preheader'})}
                                    </label>
                                    <Field
                                        name="preheader"
                                        type="text"
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                        placeholder={intl.formatMessage({id: 'emailTemplate.placeholder.preheader'})}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Section Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center gap-2">
                            {!isPrimary && (
                                <button
                                    type="button"
                                    onClick={onSetPrimary}
                                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    title={intl.formatMessage({id: 'button.setAsPrimary'})}
                                >
                                    <FontAwesomeIcon icon={faStar} className="w-3.5 h-3.5"/>
                                    {intl.formatMessage({id: 'button.setAsPrimary'})}
                                </button>
                            )}
                            {canRemove && (
                                <button
                                    type="button"
                                    onClick={onRemove}
                                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                                    title={intl.formatMessage({id: 'button.removeLanguage'})}
                                >
                                    <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5"/>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Content Type Tabs + Save Status */}
                    <div className="flex items-center justify-between border-b border-gray-200 bg-white px-2">
                        <div className="flex">
                            <ContentTabButton tab="visual" label={intl.formatMessage({id: "emailTemplate.tab.html"})}/>
                            <ContentTabButton tab="text" label={intl.formatMessage({id: "emailTemplate.preview.plainText"})}/>
                        </div>
                        <div className="pr-2">
                            <SaveButton isDirty={dirty} isSubmitting={isSubmitting} />
                        </div>
                    </div>

                    {/* Content Area - always editable */}
                    <div>
                        <EditorContent contentTab={contentTab} emailTemplateId={emailTemplateId} language={language} layoutTextContent={layoutTextContent} images={images} />
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default EmailTemplateVariantPanel;
